/** Busca do Help Center: título + resumo + conteúdo, por PALAVRA, com ranking.
 *
 *  Como era (porte do V1) e por que quebrava: procurava a frase inteira como
 *  substring, sem tirar acento e sem ordenar. "fechar caixa" virava ~todos os
 *  tutoriais (sinônimo de caixa → pdv → pedido) na ordem do arquivo, e o
 *  "Fechamento de Caixa" ficava enterrado lá embaixo; "balcao" nunca achava
 *  "Balcão". Agora:
 *   - texto e busca sem acento/caixa ("Balcão" = "balcao");
 *   - cada palavra da busca vira um radical ("fechar" e "fechamento" → "fech");
 *   - TODAS as palavras têm que aparecer (cada uma pode casar por sinônimo);
 *     se nenhum tutorial tem todas, mostra os que têm mais delas;
 *   - ranking: título > item do menu > resumo > texto; palavra digitada vale
 *     mais que sinônimo dela. */
import { tutorials, categories, SEARCH_SYNONYMS, type Category } from './data';

/** Remove tags HTML pra buscar/dar snippet no texto puro. */
export function stripHtml(html: string): string {
  return (html || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&gt;/g, '>')
    .replace(/&lt;/g, '<')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizar(s: string): string {
  return (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

function palavras(s: string): string[] {
  return normalizar(s).match(/[a-z0-9]+/g) || [];
}

/** Palavras que não dizem o assunto ("como fazer o fechamento do caixa"). */
const VAZIAS = new Set([
  'a', 'o', 'as', 'os', 'um', 'uma', 'uns', 'umas', 'de', 'da', 'do', 'das', 'dos',
  'e', 'em', 'no', 'na', 'nos', 'nas', 'ao', 'aos', 'para', 'pra', 'pro', 'com',
  'por', 'pelo', 'pela', 'como', 'que', 'se', 'meu', 'minha', 'eu', 'quero',
  'fazer', 'faco', 'onde', 'qual', 'sobre',
]);

const SUFIXOS = [
  'amentos', 'imentos', 'amento', 'imento', 'acoes', 'acao', 'ados', 'adas', 'idos', 'idas',
  'ado', 'ada', 'ido', 'ida', 'ar', 'er', 'ir', 'es', 'os', 'as', 's', 'a', 'o', 'e',
];

/** Radical bem simples: corta a terminação mais longa que deixe 4+ letras.
 *  fechar/fechamento/fechado → fech · caixa/caixas → caix · pedidos → pedid. */
function radical(w: string): string {
  if (w.length <= 4) return w;
  if (w.endsWith('ns')) return w.slice(0, -2) + 'm'; // cupons → cupom, itens → item
  for (const suf of SUFIXOS) {
    if (w.endsWith(suf) && w.length - suf.length >= 4) return w.slice(0, -suf.length);
  }
  return w;
}

/** Palavra do texto casa com o radical da busca. Radical curto ("pix", "ia")
 *  só casa inteiro — senão "pix" acharia "pixel". */
function casaPalavra(rad: string, palavraDoTexto: string): boolean {
  if (rad.length < 4) return palavraDoTexto === rad;
  return radical(palavraDoTexto).startsWith(rad) || palavraDoTexto.startsWith(rad);
}

/** Uma alternativa de busca: palavra solta (radical) ou expressão ("codigo pdv"). */
interface Alternativa {
  radical?: string;
  expressao?: string;
  peso: number; // 1 = o que a pessoa digitou · 0.6 = sinônimo
}

function alternativasDe(palavra: string): Alternativa[] {
  const alts: Alternativa[] = [{ radical: radical(palavra), peso: 1 }];
  const sinonimos = SEARCH_SYNONYMS[palavra] || SEARCH_SYNONYMS[radical(palavra)] || [];
  for (const s of sinonimos) {
    const ps = palavras(s);
    if (ps.length === 1) alts.push({ radical: radical(ps[0]), peso: 0.6 });
    else if (ps.length > 1) alts.push({ expressao: ps.join(' '), peso: 0.6 });
  }
  return alts;
}

/** Busca já picada: uma lista de alternativas por palavra digitada. */
function prepararBusca(query: string): Alternativa[][] {
  const todas = palavras(query).filter((p) => p.length >= 2);
  const uteis = todas.filter((p) => !VAZIAS.has(p));
  return (uteis.length ? uteis : todas).map(alternativasDe);
}

/** Expande o termo com os sinônimos (motoboy↔entregador, pdv↔caixa…). */
export function expandTerms(query: string): string[] {
  const out = new Set<string>();
  for (const alts of prepararBusca(query)) {
    for (const a of alts) out.add((a.radical || a.expressao) as string);
  }
  return [...out];
}

interface Campo {
  palavras: string[];
  unicas: Set<string>;
  junto: string; // ' palavra palavra ' pra achar expressão
  peso: number;
}

function campo(texto: string, peso: number): Campo {
  const ps = palavras(texto);
  return { palavras: ps, unicas: new Set(ps), junto: ' ' + ps.join(' ') + ' ', peso };
}

function casaNoCampo(a: Alternativa, c: Campo): boolean {
  if (a.expressao) return c.junto.includes(' ' + a.expressao);
  for (const p of c.unicas) if (casaPalavra(a.radical as string, p)) return true;
  return false;
}

/** Nota de uma palavra da busca num documento (0 = não aparece). */
function notaDaPalavra(alts: Alternativa[], campos: Campo[]): number {
  let melhor = 0;
  for (const a of alts) {
    for (const c of campos) {
      if (c.peso * a.peso > melhor && casaNoCampo(a, c)) melhor = c.peso * a.peso;
    }
  }
  return melhor;
}

/** Rótulo do item no menu (às vezes diz o assunto melhor que o título). */
const rotuloDoMenu: Record<string, string> = {};
for (const cat of categories) for (const it of cat.items) rotuloDoMenu[it.key] = it.label;

interface Indexado {
  key: string;
  titulo: Campo;
  menu: Campo;
  resumo: Campo;
  texto: Campo;
  textoPuro: string;
}

let indice: Indexado[] | null = null;
function indexar(): Indexado[] {
  if (indice) return indice;
  indice = Object.entries(tutorials).map(([key, t]) => {
    const textoPuro = stripHtml(t.content || '');
    return {
      key,
      titulo: campo(t.title || '', 10),
      menu: campo(rotuloDoMenu[key] || '', 8),
      resumo: campo(t.summary || '', 4),
      texto: campo(textoPuro, 2),
      textoPuro,
    };
  });
  return indice;
}

export interface SearchHit {
  key: string;
  title: string;
  hasVideo: boolean;
  /** Trecho do conteúdo com o termo destacado (<mark>). HTML seguro (gerado aqui). */
  snippet: string;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function palavraCasaBusca(w: string, busca: Alternativa[][]): boolean {
  const n = normalizar(w);
  return busca.some((alts) => alts.some((a) => a.radical && casaPalavra(a.radical, n)));
}

/** Trecho do texto em volta da 1ª palavra que casa, com destaque (<mark>). */
function montarSnippet(texto: string, busca: Alternativa[][]): string {
  const re = /[\p{L}\p{N}]+/gu;
  let primeira = -1;
  let m: RegExpExecArray | null;
  while ((m = re.exec(texto))) {
    if (palavraCasaBusca(m[0], busca)) { primeira = m.index; break; }
  }
  if (primeira === -1) return escapeHtml(texto.slice(0, 120)) + (texto.length > 120 ? '…' : '');
  const ini = Math.max(0, texto.lastIndexOf(' ', Math.max(0, primeira - 40)) + 1);
  const fimBruto = Math.min(texto.length, primeira + 100);
  const fim = fimBruto < texto.length ? Math.max(primeira + 1, texto.lastIndexOf(' ', fimBruto)) : fimBruto;
  const trecho = texto.slice(ini, fim);
  let html = '';
  let ultimo = 0;
  const reTrecho = /[\p{L}\p{N}]+/gu;
  while ((m = reTrecho.exec(trecho))) {
    if (!palavraCasaBusca(m[0], busca)) continue;
    html += escapeHtml(trecho.slice(ultimo, m.index));
    html += '<mark class="bg-yellow-200 text-yellow-900 rounded px-0.5">' + escapeHtml(m[0]) + '</mark>';
    ultimo = m.index + m[0].length;
  }
  html += escapeHtml(trecho.slice(ultimo));
  return (ini > 0 ? '…' : '') + html + (fim < texto.length ? '…' : '');
}

/** Busca profunda (título + resumo + conteúdo), ordenada por relevância.
 *  <2 chars = sem resultados. */
export function deepSearch(query: string): SearchHit[] {
  if (query.trim().length < 2) return [];
  const busca = prepararBusca(query);
  if (!busca.length) return [];

  const notas = indexar().map((doc) => {
    const campos = [doc.titulo, doc.menu, doc.resumo, doc.texto];
    const porPalavra = busca.map((alts) => notaDaPalavra(alts, campos));
    const achadas = porPalavra.filter((n) => n > 0).length;
    let total = porPalavra.reduce((s, n) => s + n, 0);
    // Todas as palavras no título (ou no menu) = é ESSE o tutorial.
    if (achadas === busca.length) {
      if (busca.every((alts) => alts.some((a) => casaNoCampo(a, doc.titulo)))) total += 15;
      else if (busca.every((alts) => alts.some((a) => casaNoCampo(a, doc.menu)))) total += 10;
    }
    return { doc, achadas, total };
  });

  const comTodas = notas.filter((n) => n.achadas === busca.length);
  const escolhidas = comTodas.length ? comTodas : notas.filter((n) => n.achadas > 0);
  escolhidas.sort((a, b) => b.achadas - a.achadas || b.total - a.total);

  return escolhidas.map(({ doc }) => {
    const tut = tutorials[doc.key];
    return {
      key: doc.key,
      title: tut.title,
      hasVideo: !!tut.video,
      snippet: montarSnippet(doc.textoPuro, busca),
    };
  });
}

/** Filtra o menu lateral (rótulo + título do tutorial, com sinônimos). Todas
 *  as palavras têm que casar; mantém a ordem do menu. Vazio = tudo. */
export function filterCategories(query: string): Category[] {
  if (!query.trim()) return categories;
  const busca = prepararBusca(query);
  if (!busca.length) return categories;
  const out: Category[] = [];
  for (const cat of categories) {
    const items = cat.items.filter((it) => {
      const campos = [campo(it.label, 1), campo(tutorials[it.key]?.title || '', 1)];
      return busca.every((alts) => notaDaPalavra(alts, campos) > 0);
    });
    if (items.length) out.push({ ...cat, items });
  }
  return out;
}

/** Acha a categoria dona de uma chave de tutorial. */
export function categoryOf(key: string): Category | undefined {
  return categories.find((c) => c.items.some((it) => it.key === key));
}
